import { useFetch } from "./useFetch.js";
import { useRepo } from "./repo.js";
import {
  fetchPage,
  fetchSession,
  fetchTree,
  fetchCommitState,
  fetchRepoStatus,
  fetchSources,
  type SourceInfo,
} from "../api.js";
import type {
  PageResponse,
  SessionInfo,
  TreeResponse,
  CommitState,
  RepoStatus,
} from "../../shared/types.js";

// Cache keys keep their original prefix (`tree`, `page:`, `commit`, `repo`) with
// the repo embedded after it, so the broad `invalidate("tree")` / `"page:"` /
// `"commit")` / `"repo")` calls across the app still match every repo's entry.

export function useSession() {
  return useFetch<SessionInfo>("session", fetchSession);
}

/** Tree of a specific repo (the sidebar accordion renders one per repo). */
export function useTree(repo: string) {
  return useFetch<TreeResponse>(repo ? `tree:${repo}` : null, () =>
    fetchTree(repo),
  );
}

export function usePage(path: string, enabled = true) {
  const repo = useRepo();
  return useFetch<PageResponse>(repo && enabled ? `page:${repo}/${path}` : null, () =>
    fetchPage(repo, path),
  );
}

export function useCommitState() {
  const repo = useRepo();
  return useFetch<CommitState>(repo ? `commit:${repo}` : null, () =>
    fetchCommitState(repo),
  );
}

export function useRepoStatus() {
  const repo = useRepo();
  return useFetch<RepoStatus>(repo ? `repo:${repo}` : null, () =>
    fetchRepoStatus(repo),
  );
}

/** Live data-source UP/DOWN (+ tables). Re-pinged on `invalidate("sources")`
 * (the data card's refresh) — that's the recovery path back to UP. */
export function useSources() {
  // On a manual refresh (the data card's ↻ → invalidate("sources")) force a
  // fresh probe; a plain load uses the server's 5-min liveness cache.
  return useFetch<SourceInfo[]>("sources", ({ refetched }) =>
    fetchSources(refetched),
  );
}
